import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AvatarModule } from '@fc-shared/ui/avatar/avatar.module';
import { QuickSearchEmployeeItem } from '@fc-search/models/quick-search';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { DialogsService } from '@fc-core/services';
import {
  ChannelType,
  ChatChannelsApiService,
} from '@fc-chats/services/chat-channels-api.service';
import { Store } from '@ngrx/store';
import { getCurrentEmployee } from '@fc-store/selectors';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';

@Component({
  selector: 'fc-qs-employee-result',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, AvatarModule, NgButtonComponent, TooltipDirective],
  template: `
    <div
      class="flex gap-12 items-center max-w-full relative py-8 pl-14 pr-16 hover:bg-secondary-50 cursor-pointer group border-l-2"
      [class.border-white]="!selected()"
      [class.border-primary-600]="selected()"
      [class.bg-secondary-50]="selected()"
      [routerLink]="['/users/main/users', result().obj.id, 'overview']"
    >
      <div class="shrink-0">
        <fc-entity-avatar
          type="employee"
          [size]="40"
          [entity]="{
            firstName: result().obj.firstName,
            lastName: result().obj.lastName,
            color: result().obj.color,
            avatar: result().obj.avatar,
          }"
        />
      </div>

      <div class="flex flex-col gap-4 grow min-w-0">
        <span class="text-sm font-medium text-grey-900 truncate">
          {{ result().obj.fullName }}
        </span>
        <div
          class="flex flex-wrap items-center gap-4 text-xs font-medium text-secondary-500 truncate"
        >
          <span>{{ result().obj.email }}</span>
          @if (
            result().obj.primaryPhoneNumber &&
            result().obj.primaryPhoneNumber !== 'None'
          ) {
            <div
              class="size-6 rounded-full bg-secondary-300 first:hidden"
            ></div>
            <span>{{ result().obj.primaryPhoneNumber }}</span>
          }
        </div>
      </div>
      <div
        class="flex gap-4 p-4 rounded-xl bg-white border-secondary-100 shadow-sm"
        [class.opacity-0]="!selected()"
        [class.opacity-100]="selected()"
        [class.group-hover:opacity-100]="!selected()"
      >
        @if (!sameEmployee()) {
          <div
            fcTooltip="Open chat"
            [showTooltip]="selected() && activeButtonIndex() === 0"
          >
            <ng-button
              variant="text"
              color="secondary"
              [focused]="activeButtonIndex() === 0"
              icon="icon-chats-outline"
              size="sm"
              iconPosition="only"
              (click)="openChat(); $event.stopPropagation()"
            />
          </div>
        }
        <div
          fcTooltip="Open in new tab"
          [showTooltip]="
            selected() && activeButtonIndex() === (sameEmployee() ? 0 : 1)
          "
        >
          <ng-button
            variant="filled"
            color="primary"
            [focused]="activeButtonIndex() === (sameEmployee() ? 0 : 1)"
            icon="icon-new-window"
            size="sm"
            iconPosition="only"
            (click)="viewDetails(); $event.stopPropagation()"
          />
        </div>
      </div>
    </div>
  `,
})
export class QsEmployeeResultComponent {
  result = input.required<QuickSearchEmployeeItem>();
  selected = input(false);
  activeButtonIndex = input(-1);
  quickAction = output();
  private router = inject(Router);
  private store = inject(Store);
  private dialogsService = inject(DialogsService);
  private channelApiService = inject(ChatChannelsApiService);
  private facade = inject(QuickSearchFacadeService);
  currentEmployee = this.store.selectSignal(getCurrentEmployee);
  sameEmployee = computed(() => {
    const currentEmployeeId = this.currentEmployee()?.id;
    return !!currentEmployeeId && currentEmployeeId === this.result().obj.id;
  });

  private canOpenChat(): boolean {
    const currentEmployeeId = this.currentEmployee()?.id;
    return !!currentEmployeeId && currentEmployeeId !== this.result().obj.id;
  }

  activateButton(index: number): void {
    if (this.sameEmployee()) {
      if (index === 0) this.viewDetails();
      return;
    }
    switch (index) {
      case 0:
        this.openChat();
        break;
      case 1:
        this.viewDetails();
        break;
    }
  }

  viewDetails(): void {
    this.quickAction.emit();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/users/main/users/' + this.result().obj.id + '/overview',
      ]),
    );
    window.open(url, '_blank');
    this.facade.clearAndClose();
  }

  async openChat(): Promise<void> {
    if (!this.canOpenChat()) return;
    this.quickAction.emit();
    const channel = await this.channelApiService.createChannel({
      members: [this.currentEmployee()?.id!, this.result().obj.id],
      type: ChannelType.DIRECT,
    });
    this.router.navigate(['/chats/direct-message', channel.id]);
    this.dialogsService.closeAll();
    this.facade.clearAndClose();
  }
}
